import { ProjectType, ShortProfile } from './Types';
import { useBackendApi } from './backendApi';
import { useObjectDatesMapper } from './Helper';
import { useCacheService } from './cacheService';
import { useEventsPublisher, useEventsSubscriber } from 'event-bus';
import {
  CONNECTION_PROFILE_CREATED,
  CONNECTION_PROFILE_DELETED,
  CONNECTION_TESTED_SUCCESSFULLY
} from './connectionProfilesService';

export enum ConnectionStatus {
  ACTIVE = 'active',
  ERROR = 'error'
}

export enum SetupStatus {
  SETUP = 'SETUP',
  PARTIALLY_SETUP = 'PARTIALLY_SETUP',
  NOT_SETUP = 'NOT_SETUP',
}

type BaseConnectionProfile = {
  id: number;
  connectionLabel: string
  createdAt: Date;
  createdBy: ShortProfile
  status: ConnectionStatus
  projectsSyncSetupStatuses: Record<ProjectType, SetupStatus>
}

type FubPermissionLevel = 'admin' | 'owner' | 'member';

type FubConnectionProfile = {
  domain: string,
  permissionLevel: FubPermissionLevel,
  _instanceUrl: string;
} & BaseConnectionProfile;

const LIST_CACHE_KEY = 'list';

export type ConnectionProfile = FubConnectionProfile;

export function useFubConnectionProfilesService() {
  const backendApi = useBackendApi();

  const cacheService = useCacheService('fubConnectionProfiles');

  const eventsPublisher = useEventsPublisher();

  useEventsSubscriber('connectionProfilesService', {
    [CONNECTION_PROFILE_DELETED]: () => {
      cacheService.clear(LIST_CACHE_KEY);
    },
    [CONNECTION_TESTED_SUCCESSFULLY]: () => {
      cacheService.clear(LIST_CACHE_KEY);
    }
  });

  const objectDatesMapper = useObjectDatesMapper<ConnectionProfile>(
    [],
    ['createdAt']
  );

  const getOauthLink = async (): Promise<string> => {
    const result = await backendApi.get('/integrations/fub/link');
    return result.link;
  };

  const createProfile = async (code: string, state: string) => {
    await backendApi.post('/integrations/profiles/fub', {
      code,
      state
    });
    cacheService.clear(LIST_CACHE_KEY);

    eventsPublisher.publish(CONNECTION_PROFILE_CREATED, {});
  };

  const getProfiles = async () => {
    const result = await cacheService.get(LIST_CACHE_KEY, async (cacheItem) => {
      cacheItem.setExpireInSeconds(120);

      return await backendApi.get('/integrations/profiles/fub');
    });

    return objectDatesMapper.mapList(result);
  };

  return {
    getOauthLink,
    createProfile,
    getProfiles
  };
}
